import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SubdomainProvider } from './contexts/SubdomainContext';
import ProtectedRoute from './components/ProtectedRoute';
import MobileNavBar from './components/MobileNavBar';
import CenterBrandingHeader from './components/branding/CenterBrandingHeader';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PasswordSetupPage from './pages/PasswordSetupPage';
import ProfilePage from './pages/ProfilePage';
import CenterCodeEntryPage from './pages/auth/CenterCodeEntryPage';
import ParentRegistrationPage from './pages/auth/ParentRegistrationPage';
import RegistrationConfirmationPage from './pages/auth/RegistrationConfirmationPage';

// User Management Pages
import UsersListPage from './pages/users/UsersListPage';
import UserDetailsPage from './pages/users/UserDetailsPage';
import CreateUserPage from './pages/users/CreateUserPage';

// Class Management Pages
import ClassesListPage from './pages/classes/ClassesListPage';
import ClassDetailsPage from './pages/classes/ClassDetailsPage';
import CreateClassPage from './pages/classes/CreateClassPage';
import HalaqaStudentsPage from './pages/classes/HalaqaStudentsPage';

// Student Management Pages
import StudentProfilePage from './pages/students/StudentProfilePage';
import ChildProfilePage from './pages/dashboards/ChildProfilePage';
import ChildAttendancePage from './pages/students/ChildAttendancePage';

// Assignment Pages
import AssignmentsListPage from './pages/assignments/AssignmentsListPage';
import AssignmentDetailsPage from './pages/assignments/AssignmentDetailsPage';
import CreateAssignmentPage from './pages/assignments/CreateAssignmentPage';
import AssignmentAssessmentPage from './pages/assignments/AssignmentAssessmentPage';
import ChildAssignmentsPage from './pages/assignments/ChildAssignmentsPage';
import StudentAssignmentPage from './pages/assignments/StudentAssignmentPage';
import StudentAssignmentsListPage from './pages/assignments/StudentAssignmentsListPage';

// Messaging Pages
import ConversationsListPage from './pages/messages/ConversationsListPage';
import ConversationDetailsPage from './pages/messages/ConversationDetailsPage';
import NewMessagePage from './pages/messages/NewMessagePage';
import ParentNewMessagePage from './pages/messages/ParentNewMessagePage';

// Settings Pages
import SettingsPage from './pages/SettingsPage';
import EditCenterPage from './pages/EditCenterPage';
import AdminEditCenterPage from './pages/AdminEditCenterPage';
import CenterDetailsPage from './pages/CenterDetailsPage';
import CentersListPage from './pages/centers/CentersListPage';

// Announcements Pages
import AnnouncementsManagementPage from './pages/announcements/AnnouncementsManagementPage';


// Admin Registration Management
import RegistrationListPage from './pages/admin/RegistrationListPage';
import RegistrationDetailPage from './pages/admin/RegistrationDetailPage';
import AdminAttendanceManagementPage from './pages/admin/AdminAttendanceManagementPage';
import AdminStudentProfile from './pages/admin/AdminStudentProfile';
import AdminStudentAssignments from './pages/admin/AdminStudentAssignments';
import AdminStudentAttendance from './pages/admin/AdminStudentAttendance';

// Reports Pages
import ReportsListPage from './pages/reports/ReportsListPage';
import ClassReportPage from './pages/reports/ClassReportPage';

// Attendance Pages
import AttendanceManagementPage from './pages/attendance/AttendanceManagementPage';
import StudentAttendancePage from './pages/students/StudentAttendancePage';

// Learning Plans Pages
import CreateLearningPlanPage from './pages/learning-plans/CreateLearningPlanPage';
import AssignmentPreviewPage from './pages/learning-plans/AssignmentPreviewPage';
import LearningPlansListPage from './pages/learning-plans/LearningPlansListPage';
import LearningPlanDetailsPage from './pages/learning-plans/LearningPlanDetailsPage';
import StudentPlanProgressPage from './pages/learning-plans/StudentPlanProgressPage';

// CSS
import './App.css';

function App() {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Update window width when resized
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <AuthProvider>
      <SubdomainProvider>
        <Router>
          <div className="app">
            {/* Center Branding Header - ALWAYS show on ALL pages to ensure consistent experience */}
            <CenterBrandingHeader />
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/setup-password" element={<PasswordSetupPage />} />
            
            {/* Registration Routes */}
            <Route path="/register" element={<ParentRegistrationPage />} />
            <Route path="/register/confirmation" element={<RegistrationConfirmationPage />} />
            <Route path="/register/code" element={<CenterCodeEntryPage />} />

            {/* Dashboard Routes */}
            <Route
              path="/dashboard"
              element={<ProtectedRoute element={<DashboardPage />} />}
            />

            {/* User Management Routes */}
            <Route
              path="/users"
              element={<ProtectedRoute element={<UsersListPage />} allowedRoles={['admin', 'super_admin']} />}
            />
            <Route
              path="/users/:id"
              element={<ProtectedRoute element={<UserDetailsPage />} allowedRoles={['admin', 'super_admin']} />}
            />
            <Route
              path="/users/new"
              element={<ProtectedRoute element={<CreateUserPage />} allowedRoles={['admin', 'super_admin']} />}
            />
            <Route
              path="/users/:id/edit"
              element={<ProtectedRoute element={<CreateUserPage />} allowedRoles={['admin', 'super_admin']} />}
            />

            {/* Class Management Routes */}
            <Route
              path="/classes/new"
              element={<ProtectedRoute element={<CreateClassPage />} allowedRoles={['admin', 'super_admin']} />}
            />
            <Route
              path="/classes/students"
              element={<ProtectedRoute element={<HalaqaStudentsPage />} allowedRoles={['admin', 'super_admin', 'teacher']} />}
            />
            <Route
              path="/classes/:id/students"
              element={<ProtectedRoute element={<HalaqaStudentsPage />} allowedRoles={['admin', 'super_admin', 'teacher']} />}
            />
            <Route
              path="/classes/:id"
              element={<ProtectedRoute element={<ClassDetailsPage />} allowedRoles={['admin', 'super_admin', 'teacher']} />}
            />
            <Route
              path="/classes"
              element={<ProtectedRoute element={<ClassesListPage />} allowedRoles={['admin', 'super_admin', 'teacher']} />}
            />
            
            {/* Student Management Routes */}
            <Route
              path="/students/:id"
              element={<ProtectedRoute element={<StudentProfilePage />} allowedRoles={['admin', 'super_admin', 'teacher']} />}
            />
            
            {/* Parent-Child Management Routes */}
            <Route
              path="/parent/children/:childId"
              element={<ProtectedRoute element={<ChildProfilePage />} allowedRoles={['parent']} />}
            />
            <Route
              path="/parent/children/:childId/assignments"
              element={<ProtectedRoute element={<ChildAssignmentsPage />} allowedRoles={['parent']} />}
            />
            <Route
              path="/parent/children/:childId/attendance"
              element={<ProtectedRoute element={<ChildAttendancePage />} allowedRoles={['parent']} />}
            />

            {/* Assignment Routes */}
            <Route
              path="/assignments/new"
              element={<ProtectedRoute element={<CreateAssignmentPage />} allowedRoles={['admin', 'teacher']} />}
            />
            <Route
              path="/assignments/:id/edit"
              element={<ProtectedRoute element={<CreateAssignmentPage />} allowedRoles={['admin', 'teacher']} />}
            />
            <Route
              path="/assignments/:id/assess"
              element={<ProtectedRoute element={<AssignmentAssessmentPage />} allowedRoles={['admin', 'teacher']} />}
            />
            <Route
              path="/assignments/student/:studentId"
              element={<ProtectedRoute element={<AssignmentsListPage />} allowedRoles={['admin', 'teacher']} />}
            />
            <Route
              path="/assignments/student-view/:id"
              element={<ProtectedRoute element={<StudentAssignmentPage />} allowedRoles={['student', 'parent']} />}
            />
            <Route
              path="/assignments/student"
              element={<ProtectedRoute element={<StudentAssignmentsListPage />} allowedRoles={['student', 'parent']} />}
            />
            <Route
              path="/assignments/:id"
              element={<ProtectedRoute element={<AssignmentDetailsPage />} allowedRoles={['admin', 'teacher', 'parent']} />}
            />
            <Route
              path="/assignments"
              element={<ProtectedRoute element={<AssignmentsListPage />} />}
            />

            {/* Learning Plans Routes */}
            <Route
              path="/learning-plans/new"
              element={<ProtectedRoute element={<CreateLearningPlanPage />} allowedRoles={['teacher']} />}
            />
            <Route
              path="/learning-plans/preview"
              element={<ProtectedRoute element={<AssignmentPreviewPage />} allowedRoles={['teacher']} />}
            />
            <Route
              path="/learning-plans/:planId"
              element={<ProtectedRoute element={<LearningPlanDetailsPage />} allowedRoles={['teacher']} />}
            />
            <Route
              path="/learning-plans/:planId/progress"
              element={<ProtectedRoute element={<StudentPlanProgressPage />} allowedRoles={['student', 'parent']} />}
            />
            <Route
              path="/learning-plans"
              element={<ProtectedRoute element={<LearningPlansListPage />} allowedRoles={['teacher']} />}
            />

            {/* Messaging Routes */}
            <Route
              path="/messages/new"
              element={<ProtectedRoute element={<NewMessagePage />} allowedRoles={['admin', 'teacher']} />}
            />
            <Route
              path="/parent/messages/new"
              element={<ProtectedRoute element={<ParentNewMessagePage />} allowedRoles={['parent']} />}
            />
            <Route
              path="/messages/:id"
              element={<ProtectedRoute element={<ConversationDetailsPage />} />}
            />
            <Route
              path="/messages"
              element={<ProtectedRoute element={<ConversationsListPage />} />}
            />

            {/* Profile Route */}
            <Route
              path="/profile"
              element={<ProtectedRoute element={<ProfilePage />} />}
            />

            {/* Settings Routes */}
            <Route
              path="/settings"
              element={<ProtectedRoute element={<SettingsPage />} />}
            />
            
            <Route
              path="/center/edit"
              element={<ProtectedRoute element={<AdminEditCenterPage />} allowedRoles={['admin']} />}
            />
            <Route
              path="/center/edit/:centerId"
              element={<ProtectedRoute element={<EditCenterPage />} allowedRoles={['super_admin']} />}
            />
            <Route
              path="/center/details/:centerId"
              element={<ProtectedRoute element={<CenterDetailsPage />} allowedRoles={['super_admin']} />}
            />
            <Route
              path="/centers/new"
              element={<ProtectedRoute element={<EditCenterPage />} allowedRoles={['super_admin']} />}
            />
            <Route
              path="/centers"
              element={<ProtectedRoute element={<CentersListPage />} allowedRoles={['super_admin']} />}
            />
            <Route
              path="/announcements"
              element={<ProtectedRoute element={<AnnouncementsManagementPage />} allowedRoles={['super_admin']} />}
            />

            {/* Admin Routes */}
            <Route
              path="/admin/registrations"
              element={<ProtectedRoute element={<RegistrationListPage />} allowedRoles={['admin', 'super_admin']} />}
            />
            <Route
              path="/admin/registrations/:id"
              element={<ProtectedRoute element={<RegistrationDetailPage />} allowedRoles={['admin', 'super_admin']} />}
            />
            <Route
              path="/admin/attendance"
              element={<ProtectedRoute element={<AdminAttendanceManagementPage />} allowedRoles={['admin', 'super_admin']} />}
            />
            <Route
              path="/admin/students/:id"
              element={<ProtectedRoute element={<AdminStudentProfile />} allowedRoles={['admin', 'super_admin']} />}
            />
            <Route
              path="/admin/students/:id/assignments"
              element={<ProtectedRoute element={<AdminStudentAssignments />} allowedRoles={['admin', 'super_admin']} />}
            />
            <Route
              path="/admin/students/:id/attendance"
              element={<ProtectedRoute element={<AdminStudentAttendance />} allowedRoles={['admin', 'super_admin']} />}
            />

            {/* Reports Routes */}
            <Route
              path="/reports"
              element={<ProtectedRoute element={<ReportsListPage />} allowedRoles={['admin', 'super_admin', 'teacher']} />}
            />
            <Route
              path="/reports/class/:id"
              element={<ProtectedRoute element={<ClassReportPage />} allowedRoles={['admin', 'super_admin', 'teacher']} />}
            />
            
            {/* Attendance Routes */}
            <Route
              path="/attendance"
              element={<ProtectedRoute element={<AttendanceManagementPage />} allowedRoles={['admin', 'teacher']} />}
            />
            <Route
              path="/students/:id/attendance"
              element={<ProtectedRoute element={<StudentAttendancePage />} allowedRoles={['admin', 'teacher']} />}
            />

            {/* Redirect root to dashboard or login */}
            <Route
              path="/"
              element={<Navigate to="/dashboard" replace />}
            />

            {/* Fallback route */}
            <Route
              path="*"
              element={<Navigate to="/dashboard" replace />}
            />
          </Routes>

          {/* Mobile Navigation - only shown on mobile devices */}
          {isMobile && <MobileNavBar />}

          {/* Screen size indicator disabled */}
          {false && process.env.NODE_ENV === 'development' && (
            <div className="screen-size-indicator">
              {windowWidth}px - {isMobile ? 'Mobile' : 'Desktop'}
            </div>
          )}
        </div>
        </Router>
      </SubdomainProvider>
    </AuthProvider>
  );
}

export default App;